import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Share2, Pencil, Trash2, CalendarHeart, EyeOff, Heart, Archive, ArchiveRestore } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ItemCard } from "@/components/ItemCard";
import { AddItemDialog } from "@/components/AddItemDialog";
import { WishlistDialog } from "@/components/WishlistDialog";
import { ShareDialog } from "@/components/ShareDialog";
import { SmartImage } from "@/components/SmartImage";

export default function WishlistDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [wl, setWl] = useState(null);
  const [error, setError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editWlOpen, setEditWlOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [thanksOpen, setThanksOpen] = useState(false);
  const [thanksMsg, setThanksMsg] = useState("");
  const [thanksBusy, setThanksBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/wishlists/${id}`);
      setWl(res.data);
    } catch (err) {
      setError(err.response?.status === 403 ? "private" : "notfound");
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const deleteWl = async () => {
    if (!window.confirm(t("wl_delete_confirm"))) return;
    try {
      await api.delete(`/wishlists/${wl.wishlist_id}`);
      toast.success(t("wl_deleted"));
      navigate("/listelerim");
    } catch { toast.error(t("error_generic")); }
  };

  const toggleArchive = async () => {
    try {
      const res = await api.post(`/wishlists/${wl.wishlist_id}/archive`);
      toast.success(res.data.is_archived ? t("wl_archived") : t("wl_unarchived"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    }
  };

  const sendThanks = async () => {
    if (!thanksMsg.trim()) { toast.error(t("thanks_empty_err")); return; }
    setThanksBusy(true);
    try {
      const res = await api.post(`/wishlists/${wl.wishlist_id}/thank-you`, { message: thanksMsg.trim() });
      toast.success(t("thanks_sent", { n: res.data.recipient_count }));
      setThanksOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    } finally { setThanksBusy(false); }
  };

  if (error) return <div className="text-center py-20 text-muted-foreground" data-testid="wishlist-error">{error === "private" ? t("wl_private") : t("wl_notfound")}</div>;
  if (!wl) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const shareUrl = `${window.location.origin}/api/share/${wl.share_token}`;
  const storyUrl = `${window.location.origin}/api/og/${wl.share_token}/story.png`;
  const days = wl.event_date ? Math.ceil((new Date(wl.event_date) - new Date()) / 86400000) : null;
  const eventPassed = days !== null && days < 0;

  return (
    <div data-testid="wishlist-detail-page">
      <div className="relative rounded-3xl overflow-hidden mb-6 bg-secondary">
        {wl.cover_image && <SmartImage src={wl.cover_image} alt="" className="w-full h-44 sm:h-60 object-cover" fallback={null} />}
        <div className={`${wl.cover_image ? "absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" : ""} flex items-end`}>
          <div className={`p-6 ${wl.cover_image ? "text-white" : ""}`}>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold" data-testid="wishlist-title">{wl.title}</h1>
            {wl.description && <p className={`text-sm mt-1 ${wl.cover_image ? "text-white/80" : "text-muted-foreground"}`}>{wl.description}</p>}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          {wl.owner && (
            <Link to={`/${wl.owner.username}`} className="flex items-center gap-2" data-testid="wishlist-owner-link">
              <Avatar className="w-8 h-8">
                <AvatarImage src={wl.owner.picture} />
                <AvatarFallback className="bg-secondary text-xs">{wl.owner.name?.[0]}</AvatarFallback>
              </Avatar>
              <span className="text-sm font-semibold">{wl.owner.name}</span>
            </Link>
          )}
          <Badge variant="secondary" className="rounded-full">{t(`priv_${wl.privacy}`)}</Badge>
          {wl.is_archived && (
            <Badge className="rounded-full bg-foreground text-white border-0" data-testid="wishlist-archived-badge">
              <Archive size={12} className="mr-1" /> {t("archived_badge")}
            </Badge>
          )}
          {days !== null && days >= 0 && (
            <Badge className="rounded-full bg-primary/10 text-primary border-0">
              <CalendarHeart size={12} className="mr-1" /> {days === 0 ? t("today") : `${days} ${t("days_left")}`}
            </Badge>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => setShareOpen(true)} data-testid="wishlist-share-btn">
            <Share2 size={15} className="mr-1" /> {t("share")}
          </Button>
          {wl.is_owner && (
            <>
              <Button className="rounded-full active:scale-95 transition-transform" onClick={() => { setEditItem(null); setAddOpen(true); }} data-testid="wishlist-add-item-btn">
                <Plus size={15} className="mr-1" /> {t("add_item")}
              </Button>
              {(eventPassed || days === null || wl.is_archived) && (
                <Button variant="outline" size="icon" className="rounded-full" onClick={toggleArchive}
                  aria-label={wl.is_archived ? t("aria_unarchive") : t("aria_archive")} data-testid="wishlist-archive-btn">
                  {wl.is_archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                </Button>
              )}
              <Button variant="outline" size="icon" className="rounded-full" onClick={() => setEditWlOpen(true)} aria-label={t("aria_edit_list")} data-testid="wishlist-edit-btn">
                <Pencil size={14} />
              </Button>
              <Button variant="outline" size="icon" className="rounded-full text-destructive" onClick={deleteWl} aria-label={t("aria_delete_list")} data-testid="wishlist-delete-btn">
                <Trash2 size={14} />
              </Button>
            </>
          )}
        </div>
      </div>

      {wl.is_owner && (
        <div className="bg-secondary/60 rounded-2xl p-4 mb-6 flex items-center gap-3 text-sm text-muted-foreground" data-testid="owner-surprise-notice">
          <EyeOff size={16} className="text-primary shrink-0" />
          {t("surprise_notice")}
        </div>
      )}

      {wl.is_owner && days !== null && days < 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="thank-you-card">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <Heart size={18} className="text-primary" />
            </div>
            <div>
              <p className="font-heading font-semibold text-sm">{t("event_passed")}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {wl.thank_you_sent ? t("thanks_done") : t("thanks_hint")}
              </p>
            </div>
          </div>
          {!wl.thank_you_sent && (
            <Button className="rounded-full active:scale-95 transition-transform shrink-0" onClick={() => setThanksOpen(true)} data-testid="thank-you-open-btn">
              <Heart size={14} className="mr-1.5" /> {t("send_thanks")}
            </Button>
          )}
        </div>
      )}

      {wl.items.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center">
          <p className="text-4xl mb-3">🎁</p>
          <p className="text-muted-foreground">{wl.is_owner ? t("no_items_owner") : t("no_items_visitor")}</p>
          {wl.is_owner && (
            <Button className="rounded-full mt-4" onClick={() => setAddOpen(true)} data-testid="wishlist-empty-add-btn">
              <Plus size={15} className="mr-1" /> {t("add_first_item")}
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {wl.items.map((item) => (
            <ItemCard key={item.item_id} item={item} isOwner={wl.is_owner} onChanged={load}
              eventPassed={eventPassed || !!wl.is_archived} archived={!!wl.is_archived}
              onEdit={(it) => { setEditItem(it); setAddOpen(true); }} />
          ))}
        </div>
      )}

      {!user && (
        <div className="mt-8 bg-foreground rounded-2xl p-6 text-center">
          <p className="text-white font-heading font-semibold mb-3">{t("guest_login_cta")}</p>
          <Link to="/giris" className="inline-block px-6 py-2.5 rounded-full bg-primary text-white text-sm font-semibold" data-testid="wishlist-guest-login-btn">{t("login_register")}</Link>
        </div>
      )}

      <AddItemDialog open={addOpen} onOpenChange={setAddOpen} wishlistId={wl.wishlist_id} editItem={editItem} onSaved={load} />
      <WishlistDialog open={editWlOpen} onOpenChange={setEditWlOpen} editWishlist={wl} onSaved={load} />
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} url={shareUrl} title={wl.title} storyUrl={storyUrl} />

      <Dialog open={thanksOpen} onOpenChange={setThanksOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{t("thanks_title")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t("thanks_desc")}</p>
          <Textarea value={thanksMsg} onChange={(e) => setThanksMsg(e.target.value)} rows={4} maxLength={500}
            className="rounded-xl" placeholder={t("thanks_ph")} data-testid="thank-you-message-input" />
          <p className="text-[10px] text-muted-foreground text-right">{thanksMsg.length}/500</p>
          <DialogFooter>
            <Button onClick={sendThanks} disabled={thanksBusy} className="rounded-full w-full h-11 font-semibold" data-testid="thank-you-send-btn">
              {thanksBusy ? t("sending") : t("send_all")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
